import { AnimatePresence, motion } from 'motion/react'
import type { CustomerOrder } from '../../customerOrdersApi'
import type { ShopLanguage, ShopLocale, ShopMessageKey } from '../../../i18n'
import {
  CustomerAreaSurfaceCard,
  EmptyStateBlock,
  customerMotionTransition,
} from '../customer-area'
import {
  PackageIcon,
  RecentOrdersSkeleton,
} from './dashboardShared'
import { DashboardRecentOrderCard } from './DashboardRecentOrderCard'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type DashboardRecentOrdersSectionProps = {
  reducedMotion: boolean
  ordersLoading: boolean
  recentOrders: CustomerOrder[]
  selectedOrderId: string | null
  language: ShopLanguage
  locale: ShopLocale
  isItalian: boolean
  t: TranslateFn
  onOpenHistory: () => void
  onOpenCatalog: () => void
  onSelectOrder: (orderId: string) => void
  onOpenOrderDetail: (orderId: string) => void
  onRequestSupport: (order: CustomerOrder) => void
}

export const DashboardRecentOrdersSection = ({
  reducedMotion,
  ordersLoading,
  recentOrders,
  selectedOrderId,
  language,
  locale,
  isItalian,
  t,
  onOpenHistory,
  onOpenCatalog,
  onSelectOrder,
  onOpenOrderDetail,
  onRequestSupport,
}: DashboardRecentOrdersSectionProps) => (
  <CustomerAreaSurfaceCard
    title={isItalian ? 'Ordini recenti' : 'Recent orders'}
    subtitle={
      isItalian ? 'Seleziona un ordine per vedere stato e dettagli.' : 'Select an order to view status and details.'
    }
    actions={
      <motion.button
        type="button"
        onClick={onOpenHistory}
        whileTap={reducedMotion ? undefined : { scale: 0.985 }}
        transition={customerMotionTransition}
        className="ca-btn-secondary inline-flex items-center px-3 py-1.5 text-xs font-semibold"
      >
        {isItalian ? 'Storico completo' : 'Full history'}
      </motion.button>
    }
  >
    <AnimatePresence mode="wait" initial={false}>
      {ordersLoading && recentOrders.length === 0 ? (
        <motion.div
          key="recent-orders-skeleton"
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
          animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
          transition={customerMotionTransition}
        >
          <RecentOrdersSkeleton />
        </motion.div>
      ) : null}

      {!ordersLoading && recentOrders.length === 0 ? (
        <motion.div
          key="recent-orders-empty"
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
          animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
          transition={customerMotionTransition}
        >
          <EmptyStateBlock
            icon={<PackageIcon className="h-4 w-4" />}
            title={t('customer.orders.emptyTitle')}
            description={t('customer.orders.emptyDescription')}
            action={
              <motion.button
                type="button"
                onClick={onOpenCatalog}
                whileTap={reducedMotion ? undefined : { scale: 0.985 }}
                transition={customerMotionTransition}
                className="ca-btn-secondary inline-flex items-center px-3 py-2 text-sm font-semibold"
              >
                {isItalian ? 'Vai al catalogo' : 'Go to catalog'}
              </motion.button>
            }
          />
        </motion.div>
      ) : null}

      {!ordersLoading && recentOrders.length > 0 ? (
        <motion.ul
          key="recent-orders-list"
          layout
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
          animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
          transition={customerMotionTransition}
          className="space-y-2"
        >
          {recentOrders.map((order) => {
            return (
              <motion.li key={order.id} layout transition={customerMotionTransition}>
                <DashboardRecentOrderCard
                  order={order}
                  selected={order.id === selectedOrderId}
                  reducedMotion={reducedMotion}
                  language={language}
                  locale={locale}
                  isItalian={isItalian}
                  t={t}
                  onSelectOrder={onSelectOrder}
                  onOpenOrderDetail={onOpenOrderDetail}
                  onOpenCatalog={onOpenCatalog}
                  onRequestSupport={onRequestSupport}
                />
              </motion.li>
            )
          })}
        </motion.ul>
      ) : null}
    </AnimatePresence>
  </CustomerAreaSurfaceCard>
)
